/**
 * Diálogos: confirmar, perguntar, avisar.
 *
 * São folhas (`mac-sheet`) do design system sobre um véu escuro, como um
 * aplicativo do sistema. Cada função devolve uma promessa — o código que
 * chama fica linear, sem callback aninhado:
 *
 *     const motivo = await perguntar({ … });
 *     if (!motivo) return;
 *
 * **Toda ação que não dá para desfazer passa por aqui**, e as que exigem
 * motivo (cancelar OS, pular etapa, estornar) não deixam confirmar com o campo
 * vazio: motivo em branco vira histórico inútil três meses depois.
 */

import { formato, type TipoExpedicao } from '@pcp/dominio';
import type { LinhaCarteira } from '@pcp/dados';
import { h, hidratar, sf, type Filho } from '@pcp/ui';

interface OpcoesBase {
  titulo: string;
  mensagem?: Filho | undefined;
  confirmar?: string | undefined;
  cancelar?: string | undefined;
  destrutivo?: boolean | undefined;
}

export interface OpcoesPergunta extends OpcoesBase {
  rotuloCampo: string;
  valorInicial?: string | undefined;
  obrigatorio?: boolean | undefined;
  /** Quando presente, vira uma lista de escolha em vez de campo de texto. */
  opcoes?: Array<{ valor: string; rotulo: string }> | undefined;
  multilinha?: boolean | undefined;
}

/** Devolve o texto (ou o valor escolhido), ou `null` se cancelou. */
export function perguntar(o: OpcoesPergunta): Promise<string | null> {
  return new Promise((resolve) => {
    let valor = o.valorInicial ?? '';

    const campo = o.opcoes
      ? h(
          'select.mac-popup',
          {
            style: { width: '100%' },
            onchange: (ev: Event) => {
              valor = (ev.target as HTMLSelectElement).value;
              validar();
            },
          },
          [
            h('option', { value: '' }, 'Escolha…'),
            ...o.opcoes.map((op) => h('option', { value: op.valor }, op.rotulo)),
          ],
        )
      : h(o.multilinha ? 'textarea.campo-e' : 'input.campo-e', {
          style: { width: '100%' },
          value: valor,
          rows: o.multilinha ? 3 : undefined,
          oninput: (ev: Event) => {
            valor = (ev.target as HTMLInputElement).value;
            validar();
          },
          onkeydown: (ev: KeyboardEvent) => {
            if (ev.key === 'Enter' && !o.multilinha) {
              ev.preventDefault();
              if (!bloqueado()) confirmar();
            }
            if (ev.key === 'Escape') cancelar();
          },
        });

    const botaoOk = h(
      `button.bt.${o.destrutivo ? 'bt-d' : 'bt-p'}`,
      { type: 'button', onclick: () => confirmar() },
      o.confirmar ?? 'Confirmar',
    );

    const bloqueado = (): boolean => !!o.obrigatorio && valor.trim() === '';
    const validar = (): void => {
      if (bloqueado()) botaoOk.setAttribute('disabled', '');
      else botaoOk.removeAttribute('disabled');
    };

    const { fechar } = abrirFolha({
      titulo: o.titulo,
      corpo: [
        o.mensagem ? h('p.t-callout.t-secondary', { style: { margin: '0 0 12px' } }, o.mensagem) : null,
        h('label.vstack.gap-1', [h('span.t-subhead.t-secondary', o.rotuloCampo), campo]),
      ],
      rodape: [
        h('button.bt.bt-s', { type: 'button', onclick: () => cancelar() }, o.cancelar ?? 'Cancelar'),
        botaoOk,
      ],
      aoFechar: () => resolve(null),
    });

    const confirmar = (): void => {
      fechar(false);
      resolve(valor.trim());
    };
    const cancelar = (): void => {
      fechar(false);
      resolve(null);
    };

    validar();
    setTimeout(() => (campo as HTMLInputElement).focus?.(), 60);
  });
}

export function confirmar(
  titulo: string,
  mensagem?: Filho,
  opcoes: { confirmar?: string | undefined; destrutivo?: boolean | undefined } = {},
): Promise<boolean> {
  return new Promise((resolve) => {
    const { fechar } = abrirFolha({
      titulo,
      corpo: mensagem ? h('p.t-callout.t-secondary', { style: { margin: 0 } }, mensagem) : null,
      rodape: [
        h('button.bt.bt-s', { type: 'button', onclick: () => { fechar(false); resolve(false); } }, 'Cancelar'),
        h(
          `button.bt.${opcoes.destrutivo ? 'bt-d' : 'bt-p'}`,
          { type: 'button', onclick: () => { fechar(false); resolve(true); } },
          opcoes.confirmar ?? 'Confirmar',
        ),
      ],
      aoFechar: () => resolve(false),
    });
  });
}

export function avisarEmFolha(titulo: string, corpo: Filho, rotulo = 'Entendi'): Promise<void> {
  return new Promise((resolve) => {
    const { fechar } = abrirFolha({
      titulo,
      corpo,
      rodape: [
        h('button.bt.bt-p', { type: 'button', onclick: () => { fechar(false); resolve(); } }, rotulo),
      ],
      aoFechar: () => resolve(),
    });
  });
}

/* ------------------------------------------------------------------ */

interface OpcoesFolha {
  titulo: string;
  corpo: Filho;
  rodape: Filho;
  largura?: number | undefined;
  aoFechar?: (() => void) | undefined;
}

export function abrirFolha(o: OpcoesFolha): { fechar: (avisar?: boolean) => void } {
  const folha = h(
    '.mac-sheet',
    { style: { width: `${o.largura ?? 420}px`, maxWidth: 'calc(100vw - 40px)' } },
    [
      h('.vstack.gap-3', { style: { padding: 'var(--sp-5)' } }, [
        h('.t-title3', o.titulo),
        o.corpo,
        h('.hstack.gap-2', { style: { justifyContent: 'flex-end', paddingTop: 'var(--sp-2)' } }, o.rodape),
      ]),
    ],
  );

  const veu = h(
    '.mac-scrim',
    {
      style: { position: 'fixed', inset: '0', display: 'grid', placeItems: 'center', zIndex: '900' },
      onclick: (ev: MouseEvent) => {
        if (ev.target === veu) fechar(true);
      },
    },
    folha,
  );

  const aoTeclar = (ev: KeyboardEvent): void => {
    if (ev.key === 'Escape') fechar(true);
  };

  const fechar = (avisar = true): void => {
    document.removeEventListener('keydown', aoTeclar);
    veu.remove();
    if (avisar) o.aoFechar?.();
  };

  document.addEventListener('keydown', aoTeclar);
  document.body.appendChild(veu);
  hidratar(veu);

  return { fechar };
}

export { sf };

/* ------------------------------------------------------------------ */
/* Expedição                                                           */
/* ------------------------------------------------------------------ */

export interface SaidaRegistrada {
  tipo: TipoExpedicao;
  quemLevou: string | null;
  transportadora: string | null;
  observacao: string | null;
}

/**
 * A folha que registra a saída da OS.
 *
 * Pré-preenche com o que o ERP combinou com o cliente — se a OS diz
 * "ENTREGAR" e traz transportadora, a folha abre em "Expedida" com a
 * transportadora escrita. Quem despacha confirma ou corrige; ninguém digita o
 * que o sistema já sabe.
 *
 * ⚠️ NENHUM CAMPO É OBRIGATÓRIO. A tentação é exigir "quem levou" para a
 * rastreabilidade ficar completa, e o efeito na prática é o contrário: o
 * balconista com o cliente na frente e o campo travado registra "cliente" e
 * segue. Um registro honesto e vazio vale mais que um preenchido para passar.
 */
export function folhaDeExpedicao(l: LinhaCarteira): Promise<SaidaRegistrada | null> {
  const combinado = (l.os.entregaTipo ?? '').toUpperCase();
  const inicial: TipoExpedicao = combinado.includes('RETIRA') ? 'retirada' : 'expedida';

  return new Promise((resolve) => {
    let tipo = inicial;

    const campoQuem = h('input.campo-e', {
      type: 'text',
      placeholder: 'Nome de quem retirou',
      autocomplete: 'off',
    }) as HTMLInputElement;

    const campoTransportadora = h('input.campo-e', {
      type: 'text',
      placeholder: 'Transportadora',
      value: l.os.transportadora ?? '',
      autocomplete: 'off',
    }) as HTMLInputElement;

    const campoObs = h('textarea.campo-e', {
      placeholder: 'Observação (opcional)',
      rows: '2',
    }) as HTMLTextAreaElement;

    const linhaQuem = h('.campo', [h('span.campo-r', 'Quem retirou'), campoQuem]);
    const linhaTransp = h('.campo', [h('span.campo-r', 'Transportadora'), campoTransportadora]);

    /* Um campo por vez: retirada pede o nome de quem levou, expedição pede a
       transportadora. Mostrar os dois convida a preencher os dois e a inventar
       um deles. */
    const ajustar = (): void => {
      linhaQuem.hidden = tipo !== 'retirada';
      linhaTransp.hidden = tipo !== 'expedida';
      (tipo === 'retirada' ? campoQuem : campoTransportadora).focus();
    };

    const seletor = h('.mac-segmented', { style: { width: '100%' } }, [
      h(`button${tipo === 'expedida' ? '.is-active' : ''}`, {
        type: 'button',
        onclick: (ev: MouseEvent) => {
          tipo = 'expedida';
          trocar(ev.currentTarget as HTMLElement);
        },
      }, 'Expedida'),
      h(`button${tipo === 'retirada' ? '.is-active' : ''}`, {
        type: 'button',
        onclick: (ev: MouseEvent) => {
          tipo = 'retirada';
          trocar(ev.currentTarget as HTMLElement);
        },
      }, 'Retirada'),
    ]);

    const trocar = (botaoEscolhido: HTMLElement): void => {
      for (const b of seletor.querySelectorAll('button')) b.classList.remove('is-active');
      botaoEscolhido.classList.add('is-active');
      ajustar();
    };

    const limpo = (v: string): string | null => (v.trim() === '' ? null : v.trim());

    const { fechar } = abrirFolha({
      titulo: `Registrar saída da OS ${l.os.numero}`,
      largura: 460,
      corpo: h('.vstack.gap-3', [
        h('.cartao-claro', [
          h('.t-body', { style: { fontWeight: '600' } }, l.os.clienteCurto),
          h('.t-callout.t-secondary', `${formato.numero(l.os.quantidade)} · ${l.os.produto}`),
          l.os.entregaTipo
            ? h('.t-subhead.t-secondary', { style: { marginTop: '4px' } },
                `Combinado no ERP: ${l.os.entregaTipo}`)
            : null,
        ]),
        seletor,
        linhaQuem,
        linhaTransp,
        h('.campo', [h('span.campo-r', 'Observação'), campoObs]),
        h('.dica', 'Registrar a saída conclui a OS: ela deixa a carteira e o quadro.'),
      ]),
      rodape: [
        h('button.bt.bt-s', {
          type: 'button',
          onclick: () => { fechar(false); resolve(null); },
        }, 'Cancelar'),
        h('button.bt.bt-p', {
          type: 'button',
          onclick: () => {
            fechar(false);
            resolve({
              tipo,
              quemLevou: tipo === 'retirada' ? limpo(campoQuem.value) : null,
              transportadora: tipo === 'expedida' ? limpo(campoTransportadora.value) : null,
              observacao: limpo(campoObs.value),
            });
          },
        }, 'Confirmar saída'),
      ],
      aoFechar: () => resolve(null),
    });

    ajustar();
  });
}
